import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faFile, faFileContract, faCalendar, faBook, faNewspaper, faGavel, faMusic, faVideo, faFileText } from '@fortawesome/free-solid-svg-icons';
import { DeleteButton, DownloadButton, EditButton } from "../../components/TableActionButtons";
import api from '../../api';
import { z } from 'zod';




// Add this helper function before getUploadsTableConfig
const getMaterialTypeIcon = (materialType) => {
    const type = materialType?.name?.toLowerCase();
    
    if (type?.includes('book')) {
        return {
            icon: faBook, 
            label: (
                <span className="inline-flex items-center px-2 py-1 bg-blue-500/20 text-blue-600 text-xs font-semibold rounded-md border border-blue-500/30 ml-2 transition-all duration-200 hover:bg-blue-500/30">
                    <div className="w-1 h-1 bg-blue-600 rounded-full mr-1.5"></div>
                    {materialType?.name}
                </span>
            )
        };
    }
    if (type?.includes('journal')) {
        return {
            icon: faNewspaper, 
            label: (
                <span className="inline-flex items-center px-2 py-1 bg-emerald-500/20 text-emerald-600 text-xs font-semibold rounded-md border border-emerald-500/30 ml-2 transition-all duration-200 hover:bg-emerald-500/30">
                    <div className="w-1 h-1 bg-emerald-600 rounded-full mr-1.5"></div>
                    {materialType?.name}
                </span>
            )
        };
    }
    if (type?.includes('case')) {
        return {
            icon: faGavel, 
            label: (
                <span className="inline-flex items-center px-2 py-1 bg-violet-500/20 text-violet-600 text-xs font-semibold rounded-md border border-violet-500/30 ml-2 transition-all duration-200 hover:bg-violet-500/30">
                    <div className="w-1 h-1 bg-violet-600 rounded-full mr-1.5"></div>
                    {materialType?.name}
                </span>
            )
        };
    }
    if (type?.includes('audio')) {
        return {
            icon: faMusic, 
            label: (
                <span className="inline-flex items-center px-2 py-1 bg-amber-500/20 text-amber-600 text-xs font-semibold rounded-md border border-amber-500/30 ml-2 transition-all duration-200 hover:bg-amber-500/30">
                    <div className="w-1 h-1 bg-amber-600 rounded-full mr-1.5"></div>
                    {materialType?.name}
                </span>
            )
        };
    }
    if (type?.includes('video')) {
        return {
            icon: faVideo, 
            label: (
                <span className="inline-flex items-center px-2 py-1 bg-rose-500/20 text-rose-600 text-xs font-semibold rounded-md border border-rose-500/30 ml-2 transition-all duration-200 hover:bg-rose-500/30">
                    <div className="w-1 h-1 bg-rose-600 rounded-full mr-1.5"></div>
                    {materialType?.name}
                </span>
            )
        };
    }
    if (type?.includes('report')) {
        return {
            icon: faFileText, 
            label: (
                <span className="inline-flex items-center px-2 py-1 bg-slate-500/20 text-slate-600 text-xs font-semibold rounded-md border border-slate-500/30 ml-2 transition-all duration-200 hover:bg-slate-500/30">
                    <div className="w-1 h-1 bg-slate-600 rounded-full mr-1.5"></div>
                    {materialType?.name}
                </span>
            )
        };
    }
    if (type?.includes('cutting')) {
        return {
            icon: faNewspaper, 
            label: (
                <span className="inline-flex items-center px-2 py-1 bg-cyan-500/20 text-cyan-600 text-xs font-semibold rounded-md border border-cyan-500/30 ml-2 transition-all duration-200 hover:bg-cyan-500/30">
                    <div className="w-1 h-1 bg-cyan-600 rounded-full mr-1.5"></div>
                    {materialType?.name}
                </span>
            )
        };
    }
    
    // Default case
    return {
        icon: faFileContract, 
        label: (
            <span className="inline-flex items-center px-2 py-1 bg-gray-500/20 text-gray-600 text-xs font-semibold rounded-md border border-gray-500/30 ml-2 transition-all duration-200 hover:bg-gray-500/30">
                <div className="w-1 h-1 bg-gray-600 rounded-full mr-1.5"></div>
                {materialType?.name}
            </span>
        )
    };
};


export const getUploadsTableConfig = (truncate, handleEdit, handleDelete, handleDownload, downloadingId = null) => [
    {
        header: () => (
            <span className="flex items-center">
                <FontAwesomeIcon icon={faFile} className="w-4 h-4 mr-2" />
                TITLE AND DESCRIPTION
            </span>
        ),
        accessorKey: 'title',
        cell: ({ row }) => (
            <div className="flex flex-col justify-start items-start truncate">
                <div className="text-left flex items-center">
                    <FontAwesomeIcon icon={getMaterialTypeIcon(row.original.material_type).icon} className="w-4 h-4 mr-2" />
                    <span className="font-medium">{truncate(row.original.title)}</span> 
                    {getMaterialTypeIcon(row.original.material_type).label}
                </div>
                <div className="text-left mt-1">
                    {row.original.description && (
                        <span className="text-gray-500 text-sm line-clamp-2">
                            {truncate(row.original.description)}
                        </span>
                    )}
                </div>
            </div>
        )
    },
    {
        header: () => (
            <span className="flex items-center">
                <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 mr-2" />
                DATE
            </span>
        ),
        accessorKey: 'date',
        cell: ({ row }) => {
            const date = new Date(row.original.date);
            return (
                <div className="text-left">
                    {date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })}
                </div>
            );
        }
    },

    {
        header: () => (
            <span className="flex items-center justify-center">
                <FontAwesomeIcon icon={faCog} className="w-4 h-4 mr-2" />
                ACTIONS
            </span>
        ),
        id: 'actions',
        cell: ({ row }) => {
            // Determine if this specific row is audio/video based on its material type
            const isAudioOrVideo = row.original.material_type?.name?.toLowerCase().includes('audio') || 
                                  row.original.material_type?.name?.toLowerCase().includes('video');
            return (
                <div className="flex items-center justify-center gap-3">
                    <EditButton title="Edit Upload" onClick={()=>handleEdit(row.original)} row={row} />
                    <DeleteButton title="Delete Upload" onClick={()=>handleDelete(row.original)} row={row} />
                    {!isAudioOrVideo && (
                    <DownloadButton 
                        title="Download Upload" 
                        onClick={()=>handleDownload(row.original)} 
                        row={row} 
                        isLoading={downloadingId === row.original.id}
                    />
                    )}
                </div>
            );
        }
    }
];

export const PAGE_CONFIG = {
    BREADCRUMB: [
        { label: 'Home', link: '/' },
        { label: 'Administration', link: null },
        { label: 'Upload', link: '/upload' }
    ],
    PAGE_TITLE: 'Upload',
    GET_QUERY_KEY: 'upload',
    SEARCH_DESCRIPTION: 'Search for a upload by title, description, or material type',
    SEARCH_PLACEHOLDER: 'No uploads found',
    API_CALL: () => api.upload.getUploads(),
    DEFAULT_ERROR_MESSAGE: 'Failed to fetch upload',
    SELECT: (data) => data?.data,
    //Delete
    DELETE_API_CALL: (id) => api.upload.deleteUpload(id),
    DELETE_ERROR_MESSAGE: 'Failed to delete upload',
    DELETE_SUCCESS_MESSAGE: 'Upload deleted successfully',
    //Create
    CREATE_API_CALL: (data) => api.upload.createUpload(data),
    CREATE_ERROR_MESSAGE: 'Failed to create upload',
    CREATE_SUCCESS_MESSAGE: 'Upload created successfully',

    //Get Material Types
    GET_MATERIAL_TYPES_API_CALL: () => api.materialTypes.getMaterialTypes(),
    GET_MATERIAL_TYPES_ERROR_MESSAGE: 'Failed to fetch material types',
    GET_MATERIAL_TYPES_SUCCESS_MESSAGE: 'Material types fetched successfully'
};

export const INITIAL_FORM_STATE = {
    title: '',
    date: '',
    description: '',
    material_type_id: '',
    url: '',
    manual: '',
    thumbnail: ''
};

export const INITIAL_FILES_STATE = {
    manual: null,
    thumbnail: null
};

export const INITIAL_FORM_ERRORS = {
    title: '',
    date: '',
    description: '',
    material_type_id: '',
    url: '',
    manual: '',
    thumbnail: ''
};

export const getFormConfig = (formData, handleInputChange, handleFileChange, errors, isEdit, materialType = []) => [
    {
        formControl: 'input',
        autoComplete: 'off',
        label: 'Title',
        id: 'title',
        placeholder: 'Enter Title',
        name: 'title',
        type: 'text',
        required: true,
        value: formData.title,
        onChange: (e) => handleInputChange('title', e.target.value),
        error: errors.title

        // error: touched.firstname ? errors.firstname : ''
    },
    {
        formControl: 'date',
        autoComplete: 'off',
        label: 'Date',
        id: 'date',
        placeholder: 'Select Date',
        name: 'date',
        type: 'text',
        required: true,
        value: formData.date,
        onChange: (e) => handleInputChange('date', e.target.value)
        // onBlur: () => handleBlur('lastname'),
        // error: touched.lastname ? errors.lastname : ''
    },
    {
        formControl: 'select',
        autoComplete: 'off',
        label: 'Type',
        id: 'material_type_id',
        placeholder: 'Select Material Type',
        name: 'material_type_id',
        value: formData.material_type_id,
        options: materialType.map((item) => ({ label: item.name, value: item.id })),
        onChange: handleInputChange
    },
    {
        formControl: 'textarea',
        autoComplete: 'off',
        label: 'Description',
        id: 'description',
        placeholder: 'Enter Description',
        name: 'description',
        type: 'text',
        required: true,
        value: formData.description,
        onChange: (e) => handleInputChange('description', e.target.value)
        // onBlur: () => handleBlur('email'),
    },
    {
        formControl: 'input',
        autoComplete: 'off',
        label: 'Manual',
        id: 'manual',
        placeholder: 'Upload Manual',
        name: 'manual',
        type: 'file',
        required: false,
        value: formData.manual,
        onChange: (e) => {
            handleFileChange('manual', e.target.files[0]);
            handleInputChange('manual', e.target.value);
        },
        hint: 'Accepted formats: PDF, DOC, DOCX (Max size: 10MB)',
        accept: '.pdf,.doc,.docx',
        error: errors.manual
    },
    {
        formControl: 'input',
        autoComplete: 'off',
        label: 'Thumbnail',
        id: 'thumbnail',
        placeholder: 'Upload Thumbnail',
        name: 'thumbnail',
        type: 'file',
        required: false,
        value: formData.thumbnail,
        onChange: (e) => {
            handleFileChange('thumbnail', e.target.files[0]);
            handleInputChange('thumbnail', e.target.value);
        },
        hint: 'Accepted formats: JPG, PNG, JPEG (Max size: 2MB)',
        accept: '.jpg,.jpeg,.png',
        error: errors.thumbnail
    },
    {
        formControl: 'input',
        autoComplete: 'off',
        label: 'Url',
        id: 'url',
        placeholder: 'Enter Content Url',
        name: 'url',
        type: 'text',
        required: false,
        value: formData.url,
        onChange: (e) => handleInputChange('url', e.target.value),
        error: errors.url

        // error: touched.firstname ? errors.firstname : ''
    }
];
export const UPLOAD_SCHEMA = z.object({
    title: z.string().min(3, 'Title must be at least 3 characters'),
    description: z.string().min(3, 'Description must be at least 3 characters'),
    date: z.coerce.date().or(z.string().min(3, 'Invalid date format')),
   // material_type_id: z.string().min(1, 'Material type is required'),
});

export const FILE_SCHEMA = z.object({
    manual: z.instanceof(File, 'Manual must be a file').optional(),
    thumbnail: z.instanceof(File, 'Thumbnail must be a file').optional()
});
